import React, { useState, useEffect, useCallback, useMemo, useRef, memo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  MdKeyboardArrowLeft as ArrowLeftIcon,
} from 'react-icons/md';

import HelpEditor from './helpEditor.component';
import plainSerializer from 'slate-plain-serializer';
import useSlateValue from './hooks/useSlateValue';
import { HelpContentPropTypes } from '../shared/proptypes';

import NavigationPrompt, {
  DISCARD_UNSAVED_CHANGES_MESSAGE,
  DISCARD_UNSAVED_CHANGES_TITLE,
} from '../../shared/navigationPrompt.component';
import Spinner from '../../shared/spinner.component';
import {
  helpContentSelector,
  helpLoadingSelector,
  helpUpdatingSelector,
} from '../shared/store/selectors';

import {
  fetchHelpContent,
  updateHelpContent,
  clearHelpContent,
} from '../shared/store/actions';

import { plugins } from './editorPlugins';
import { IconGroup } from './helpContentIconGroup.component';
import { HelpContentSettingsPopup } from '../shared/HelpContentSettings.component';
import { EDIT_HELP_SETTINGS_POPUP } from '../shared/constants';

// remove this workaround, after only correct content is in the DB
const parseContent = string => {
  try {
    return JSON.parse(string);
  } catch (e) {
    return {};
  }
};

const useEditableText = () => {
  const inputRef = useRef(null);
  const [value, setValue] = useState('');

  const [isInputVisible, setInputVisible] = useState(false);
  const showInput = useCallback(() => setInputVisible(true), []);
  const hideInput = useCallback(() => setInputVisible(false), []);

  useEffect(() => {
    if (isInputVisible && inputRef && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isInputVisible]);

  return { inputRef, isInputVisible, showInput, hideInput, value, setValue };
};

const HelpContent = ({
  helpContentTitle,
  helpContentId,
  helpContent,
  onClose,
  fetchHelpContent,
  clearHelpContent,
  updateHelpContent,
  helpUpdating,
  readOnly,
  history,
}) => {
  const {
    inputRef,
    isInputVisible,
    showInput,
    hideInput,
    value: newTitle,
    setValue: setNewTitle,
  } = useEditableText();
  useEffect(() => {
    if (helpContent) {
      setNewTitle(helpContent.title);
    }
  }, [helpContent, setNewTitle]);
  const handleTitleChange = useCallback(event => setNewTitle(event.target.value), [setNewTitle]);

  const initialSlateValue = useMemo(() => helpContent && parseContent(helpContent.content), [helpContent]);
  const [value, setValue, hasValueChanged, resetHasValueChanged] = useSlateValue(initialSlateValue);

  const [contentSettings, setContentSettings] = useState(null);

  useEffect(() => {
    if (helpContent) {
      setContentSettings({
        clients: helpContent.clients,
      });
    }
  }, [helpContent]);

  const handleSaveClick = useCallback(() => {
    const content = JSON.stringify(value.toJSON());
    const contentText = plainSerializer.serialize(value);

    resetHasValueChanged();
    updateHelpContent(helpContentId, {
      title: newTitle,
      content,
      contentText,
      ...contentSettings,
    });
  }, [value, newTitle, helpContentId, updateHelpContent, resetHasValueChanged, contentSettings]);

  useEffect(() => {
    fetchHelpContent(helpContentId, history);
    return () => clearHelpContent();
  }, [fetchHelpContent, helpContentId, clearHelpContent, history]);

  if (!helpContent) {
    return (
      <div className="HelpContent">
        {
          helpContentTitle
            ? (
              <h4 className="popup-header">
                <div className="header">
                  <div className="popup-header-back-arrow" onClick={history.goBack}>
                    <ArrowLeftIcon />
                  </div>
                  <h5>{helpContentTitle}</h5>
                  <IconGroup updating />
                </div>
              </h4>
            )
            : null
        }
        <div className="popup-body">
          <Spinner />
        </div>
      </div>
    );
  }

  return (
    <div className="HelpContent">
      {
        Boolean(contentSettings) && (
          <HelpContentSettingsPopup
            settings={contentSettings}
            onChange={setContentSettings}
            popupId={EDIT_HELP_SETTINGS_POPUP}
            Footer={(
              <div className="alert alert-info">
                Use the Help editor&apos;s &quot;Save&quot; button to save these settings
              </div>
            )}
          />
        )
      }
      <h4 className="popup-header">
        <div className="header">
          <div className="popup-header-back-arrow" onClick={history.goBack}>
            <ArrowLeftIcon/>
          </div>
          {isInputVisible && !readOnly
            ? (
              <input
                ref={inputRef}
                className="HelpContent-titleInput"
                value={newTitle}
                onChange={handleTitleChange}
                onBlur={hideInput}
              />
            )
            : <h5 onClick={showInput}>{newTitle}</h5>}
          <IconGroup
            onClose={onClose}
            onSave={handleSaveClick}
            readOnly={readOnly}
            updating={helpUpdating}
            warning={hasValueChanged}
          />
        </div>
      </h4>
      <div className="popup-body">
        <HelpEditor
          value={value}
          onChange={setValue}
          readOnly={readOnly}
          plugins={plugins}
        />
        <NavigationPrompt
          show={hasValueChanged}
          title={DISCARD_UNSAVED_CHANGES_TITLE}
          message={DISCARD_UNSAVED_CHANGES_MESSAGE}
        />
      </div>
    </div>
  );
};

HelpContent.propTypes = {
  helpContentId: PropTypes.number.isRequired,
  helpContentTitle: PropTypes.string,
  helpContent: HelpContentPropTypes,
  onClose: PropTypes.func.isRequired,
  fetchHelpContent: PropTypes.func.isRequired,
  clearHelpContent: PropTypes.func.isRequired,
  updateHelpContent: PropTypes.func.isRequired,
  helpUpdating: PropTypes.bool.isRequired,
  readOnly: PropTypes.bool.isRequired,
  helpLoading: PropTypes.bool.isRequired,
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
  }).isRequired,
};

function mapStateToProps(state) {
  return {
    helpContent: helpContentSelector(state),
    helpLoading: helpLoadingSelector(state),
    helpUpdating: helpUpdatingSelector(state),
  };
}

export default connect(
  mapStateToProps,
  {
    fetchHelpContent,
    updateHelpContent,
    clearHelpContent,
  }
)(memo(HelpContent));
